import type { Project, ProjectStatistics, ReportCampaignRow } from '../../types';
import { statusLabel } from '../respondents/respondentStatus';

/** The browser's time zone (an IANA name such as "Asia/Kolkata"), sent as `tz` so the server cuts days where the user is. UTC if unknown. */
export const browserTimeZone = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (e) { return 'UTC'; }
};

/**
 * A line of the Reports page built from one campaign's own statistics (GET /project/{id}/statistics): used only when the one-call report
 * route is missing. The figures are the same; the ones only respondent sessions give are null before there are any.
 */
export function rowFromStatistics(project: Project, stats: ProjectStatistics | null, error?: string): ReportCampaignRow {
  const sessions = Boolean(stats && stats.source === 'sessions');
  return {
    id: project.id,
    projectId: project.projectId,
    name: project.name,
    projectStatus: project.projectStatus,
    startDate: project.startDate,
    endDate: project.endDate,
    source: stats ? stats.source : undefined,
    complete: stats ? stats.complete : 0,
    screenout: stats ? stats.screenout : 0,
    quotaFull: stats ? stats.quotaFull : 0,
    total: stats ? stats.total : 0,
    inSurvey: sessions && stats ? stats.inSurvey ?? null : null,
    timedOut: sessions && stats ? stats.timedOut ?? null : null,
    qualityTerminate: sessions && stats ? stats.qualityTerminate ?? null : null,
    entries: sessions && stats ? stats.entries ?? null : null,
    wantedCompletes: stats ? stats.wantedCompletes : null,
    remainingCompletes: stats ? stats.remainingCompletes : null,
    progress: stats ? stats.progress : null,
    incidenceRate: stats ? stats.incidenceRate : null,
    quotaFullRate: stats ? stats.quotaFullRate : null,
    dropOutRate: sessions && stats ? stats.dropOutRate ?? null : null,
    conversionRate: sessions && stats ? stats.conversionRate ?? null : null,
    medianLoiMinutes: sessions && stats ? stats.medianLoiMinutes ?? null : null,
    lastActivityAt: null,
    ...(error ? { error } : {}),
  };
}

const CSV_COLUMNS: { header: string; value: (r: ReportCampaignRow) => unknown }[] = [
  { header: 'campaignId', value: (r) => r.id },
  { header: 'projectId', value: (r) => r.projectId },
  { header: 'name', value: (r) => r.name },
  { header: 'status', value: (r) => r.projectStatus },
  { header: 'source', value: (r) => r.source },
  { header: 'wantedCompletes', value: (r) => r.wantedCompletes },
  { header: 'complete', value: (r) => r.complete },
  { header: 'screenout', value: (r) => r.screenout },
  { header: 'quotaFull', value: (r) => r.quotaFull },
  { header: 'inSurvey', value: (r) => r.inSurvey },
  { header: 'timedOut', value: (r) => r.timedOut },
  { header: 'qualityTerminate', value: (r) => r.qualityTerminate },
  { header: 'entries', value: (r) => r.entries },
  { header: 'progressPercent', value: (r) => r.progress },
  { header: 'incidenceRatePercent', value: (r) => r.incidenceRate },
  { header: 'quotaFullRatePercent', value: (r) => r.quotaFullRate },
  { header: 'dropOutRatePercent', value: (r) => r.dropOutRate },
  { header: 'conversionRatePercent', value: (r) => r.conversionRate },
  { header: 'medianLoiMinutes', value: (r) => r.medianLoiMinutes },
  { header: 'lastActivityAt', value: (r) => r.lastActivityAt },
];

/** A CSV cell: empty for a missing value (never a made-up zero), quoted when it holds a comma, quote or line break. */
export const csvCell = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** The campaign summary as CSV, exactly the rows shown, with the figures as the server sent them. A campaign with no figures is blank. */
export function summaryToCsv(rows: ReportCampaignRow[]): string {
  const lines = [CSV_COLUMNS.map((c) => c.header).join(',')];
  rows.forEach((r) => lines.push(CSV_COLUMNS.map((c) => csvCell(r.error ? (c.header === 'campaignId' || c.header === 'projectId' || c.header === 'name' || c.header === 'status' ? c.value(r) : '') : c.value(r))).join(',')));
  return lines.join('\r\n');
}

export interface FunnelStep {
  key: string;
  label: string;
  value: number;
}

/**
 * How the respondents of a campaign ended. With respondent sessions (`source` "sessions") the base is everyone who entered
 * and the steps are every status, including the ones still in the survey; before that the only figures are the survey
 * redirects (complete, screen-out, quota full) and the base is their total.
 */
export function funnelOf(stats: ProjectStatistics): { base: number; baseLabel: string; steps: FunnelStep[] } {
  if (stats.source === 'sessions') {
    const steps: FunnelStep[] = [
      { key: 'started', label: statusLabel('started'), value: stats.inSurvey ?? 0 },
      { key: 'complete', label: statusLabel('complete'), value: stats.complete },
      { key: 'screenout', label: statusLabel('screenout'), value: stats.screenout },
      { key: 'quotaFull', label: statusLabel('quotaFull'), value: stats.quotaFull },
      { key: 'timedOut', label: statusLabel('timedOut'), value: stats.timedOut ?? 0 },
      { key: 'qualityTerminate', label: statusLabel('qualityTerminate'), value: stats.qualityTerminate ?? 0 },
      { key: 'possibleDuplicate', label: statusLabel('possibleDuplicate'), value: stats.possibleDuplicate ?? 0 },
    ];
    return { base: stats.entries ?? stats.total, baseLabel: 'entered the survey', steps };
  }
  return {
    base: stats.total,
    baseLabel: 'came back through the survey links',
    steps: [
      { key: 'complete', label: statusLabel('complete'), value: stats.complete },
      { key: 'screenout', label: statusLabel('screenout'), value: stats.screenout },
      { key: 'quotaFull', label: statusLabel('quotaFull'), value: stats.quotaFull },
    ],
  };
}

/** Today's date as YYYY-MM-DD (for file names). */
export const today = () => new Date().toISOString().slice(0, 10);

/** True when both dates are given and `from` is after `to` (the server would answer 400). */
export const datesOutOfOrder = (from: string, to: string) => Boolean(from && to && from > to);
